import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import OrderTypeBadge from "@/components/OrderTypeBadge";
import EditOrderDialog from "@/components/EditOrderDialog";
import { toast } from "sonner";
import { Eye, ExternalLink, Check, Copy, Pencil } from "lucide-react";

const DELIVERY_STATUSES = ["Pending", "In Transit", "Delivered", "Undelivered", "Cancelled"];
const FULFILLMENT_STATUSES = ["Pending", "Fulfilled"];

function copy(text) {
  if (!text) return;
  navigator.clipboard?.writeText(text);
  toast.success("Copied");
}

// Editable cell for delivery link (saves on blur if changed)
function LinkCell({ value, onSave, testid }) {
  const [val, setVal] = useState(value || "");
  const initial = useRef(value || "");

  useEffect(() => {
    setVal(value || "");
    initial.current = value || "";
  }, [value, setVal]);

  const commit = () => {
    if (val !== initial.current) {
      onSave(val);
      initial.current = val;
    }
  };

  return (
    <div className="flex items-center gap-1 min-w-[200px]">
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
        placeholder="Paste tracking link"
        className="h-8 text-xs"
        data-testid={testid}
      />
      {val && (
        <a href={val} target="_blank" rel="noreferrer" title="Open">
          <Button variant="ghost" size="icon" className="h-7 w-7"><ExternalLink className="w-3.5 h-3.5" /></Button>
        </a>
      )}
    </div>
  );
}

function CopyText({ text, className = "" }) {
  if (!text) return <span className="text-muted-foreground">—</span>;
  return (
    <button
      type="button"
      onClick={() => copy(text)}
      title="Click to copy"
      className={`text-left hover:bg-muted px-1 -mx-1 rounded transition-colors ${className}`}
    >
      {text}
    </button>
  );
}

export default function OperationsOrders() {
  const [orders, setOrders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [dStatus, setDStatus] = useState("all");
  const [agent, setAgent] = useState("all");
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const PAGE = 100;

  const buildParams = useCallback(() => {
    const params = {};
    if (fStatus !== "all") params.fulfillment_status = fStatus;
    if (dStatus !== "all") params.delivery_status = dStatus;
    if (agent !== "all") params.agent_id = agent;
    if (search) params.search = search;
    return params;
  }, [fStatus, dStatus, agent, search]);

  const load = useCallback(async (append = false) => {
    // Paginated load — the old code fetched up to 2000 rows in one shot which
    // froze the ops browser once the table hit a couple thousand orders.
    const params = { ...buildParams(), limit: PAGE, offset: append ? orders.length : 0 };
    setLoading(true);
    try {
      const [{ data }, { data: cnt }] = await Promise.all([
        api.get("/orders", { params }),
        append ? Promise.resolve({ data: { total } }) : api.get("/orders/count", { params: buildParams() }),
      ]);
      setOrders((prev) => (append ? [...prev, ...data] : data));
      if (!append) setTotal(cnt.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [buildParams, orders.length, total]);

  useEffect(() => { load(false); }, [fStatus, dStatus, agent]);   // reload on filter change

  useEffect(() => {
    api.get("/users/agents").then(({ data }) => setAgents(data)).catch(() => {});
  }, []);

  const onSearch = (e) => { e.preventDefault(); load(false); };

  const patchOrder = async (id, body) => {
    try {
      const { data } = await api.put(`/orders/${id}/fulfill`, body);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, ...data } : o)));
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateDelivery = async (id, status) => {
    try {
      const { data } = await api.put(`/orders/${id}/delivery`, { delivery_status: status });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, ...data } : o)));
      toast.success("Delivery status updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight">Order Fulfillment</h1>
        <p className="text-sm text-muted-foreground">All manual orders. Click any text to copy. Edit fulfillment, link and delivery status inline.</p>
      </div>

      <Card className="p-4">
        <form onSubmit={onSearch} className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Input placeholder="Search order ID, customer, phone..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ops-search" />
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger data-testid="ops-filter-agent"><SelectValue placeholder="Sales BDM" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All BDMs</SelectItem>
              {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger data-testid="ops-filter-fulfillment"><SelectValue placeholder="Fulfillment" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Fulfillment</SelectItem>
              {FULFILLMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={dStatus} onValueChange={setDStatus}>
            <SelectTrigger data-testid="ops-filter-delivery"><SelectValue placeholder="Delivery" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Delivery</SelectItem>
              {DELIVERY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="submit" variant="outline" data-testid="ops-search-btn">Search</Button>
        </form>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[140px] sticky left-0 z-20 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">Order ID</TableHead>
                <TableHead className="min-w-[120px]">BDM</TableHead>
                <TableHead className="min-w-[140px]">Customer</TableHead>
                <TableHead className="min-w-[120px]">Phone</TableHead>
                <TableHead className="min-w-[200px]">Address</TableHead>
                <TableHead className="min-w-[100px]">City</TableHead>
                <TableHead className="min-w-[100px]">State</TableHead>
                <TableHead className="min-w-[100px]">Pincode</TableHead>
                <TableHead className="min-w-[180px]">Email</TableHead>
                <TableHead className="min-w-[200px]">Products</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Order Type</TableHead>
                <TableHead>Payment Type</TableHead>
                <TableHead>Total</TableHead>
                <TableHead className="min-w-[120px]">Discount</TableHead>
                <TableHead className="min-w-[140px]">Other Charges</TableHead>
                <TableHead className="bg-amber-50 font-semibold text-amber-900">Amount to Collect</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead className="min-w-[150px]">Notes</TableHead>
                <TableHead className="min-w-[140px]">Fulfillment</TableHead>
                <TableHead className="min-w-[260px]">Delivery Link</TableHead>
                <TableHead className="min-w-[150px]">Delivery Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={24} className="text-center text-muted-foreground py-8">No orders</TableCell></TableRow>
              )}
              {orders.map((o) => {
                const qty = (o.items || []).reduce((s, i) => s + Number(i.quantity || 0), 0);
                const products = (o.items || []).map((i) => `${i.sku_name} x${i.quantity}`).join(", ");
                return (
                  <TableRow key={o.id} data-testid={`ops-row-${o.order_id}`}>
                    <TableCell className="font-mono text-xs sticky left-0 z-10 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]"><CopyText text={o.order_id} /></TableCell>
                    <TableCell className="text-xs">{o.agent_name}</TableCell>
                    <TableCell className="font-medium text-xs"><CopyText text={o.customer_name} /></TableCell>
                    <TableCell className="text-xs"><CopyText text={o.phone} /></TableCell>
                    <TableCell className="text-xs max-w-[260px] whitespace-normal break-words"><CopyText text={o.address} /></TableCell>
                    <TableCell className="text-xs"><CopyText text={o.city} /></TableCell>
                    <TableCell className="text-xs"><CopyText text={o.state} /></TableCell>
                    <TableCell className="text-xs"><CopyText text={o.pincode} /></TableCell>
                    <TableCell className="text-xs"><CopyText text={o.email} /></TableCell>
                    <TableCell className="text-xs max-w-[260px] whitespace-normal break-words" title={products}>{products || "—"}</TableCell>
                    <TableCell className="text-xs">{qty}</TableCell>
                    <TableCell><OrderTypeBadge name={o.order_type_tag_name} color={o.order_type_tag_color} /></TableCell>
                    <TableCell><StatusBadge status={o.order_type} /></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">₹{Number(o.total_order_amount).toFixed(2)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap" data-testid={`ops-discount-${o.order_id}`}>
                      {Number(o.discount_percentage || 0) > 0 ? (
                        <span>{Number(o.discount_percentage).toFixed(2)}% (₹{Number(o.discount_amount || 0).toFixed(2)})</span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs whitespace-normal break-words max-w-[200px]" data-testid={`ops-other-charges-${o.order_id}`}>
                      {Number(o.other_charges_amount || 0) > 0 ? (
                        <div>
                          <div className="whitespace-nowrap">₹{Number(o.other_charges_amount).toFixed(2)}</div>
                          {o.other_charges_note && <div className="text-muted-foreground">{o.other_charges_note}</div>}
                        </div>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm font-semibold whitespace-nowrap bg-amber-50 text-amber-900" data-testid={`ops-collect-${o.order_id}`}>₹{Number(o.amount_to_collect || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      {o.payment_screenshot ? (
                        <a href={`/api/files/${o.payment_screenshot}`} target="_blank" rel="noreferrer" className="text-xs underline text-blue-600">View</a>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs max-w-[220px] whitespace-normal break-words"><CopyText text={o.notes} /></TableCell>
                    <TableCell>
                      <Select
                        value={o.fulfillment_status}
                        onValueChange={(v) => patchOrder(o.id, { fulfillment_status: v })}
                      >
                        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`ops-fulfill-${o.order_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FULFILLMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <LinkCell
                        value={o.delivery_link}
                        onSave={(v) => patchOrder(o.id, { delivery_link: v })}
                        testid={`ops-link-${o.order_id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={o.delivery_status}
                        onValueChange={(v) => updateDelivery(o.id, v)}
                      >
                        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`ops-delivery-${o.order_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DELIVERY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditing(o)} data-testid={`ops-edit-${o.order_id}`} title="Edit order">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDetail(o)} data-testid={`ops-view-${o.order_id}`} title="View details">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between p-3 border-t bg-muted/20 text-xs">
          <div data-testid="ops-showing">
            Showing {orders.length} of {total} orders
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={loading || orders.length >= total}
            onClick={() => load(true)}
            data-testid="ops-load-more"
          >
            {loading ? "Loading…" : orders.length >= total ? "All loaded" : "Load more"}
          </Button>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle>Order {detail.order_id}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-xs text-muted-foreground">BDM</div><div>{detail.agent_name}</div></div>
                  <div><div className="text-xs text-muted-foreground">Customer</div><div>{detail.customer_name}</div></div>
                  <div><div className="text-xs text-muted-foreground">Phone</div><div>{detail.phone}</div></div>
                  <div><div className="text-xs text-muted-foreground">Order Type</div><OrderTypeBadge name={detail.order_type_tag_name} color={detail.order_type_tag_color} /></div>
                  <div><div className="text-xs text-muted-foreground">Payment Type</div><StatusBadge status={detail.order_type} /></div>
                  <div className="col-span-2"><div className="text-xs text-muted-foreground">Address</div><div>{detail.address}</div></div>
                  <div><div className="text-xs text-muted-foreground">City</div><div>{detail.city || "—"}</div></div>
                  <div><div className="text-xs text-muted-foreground">State</div><div>{detail.state || "—"}</div></div>
                  <div><div className="text-xs text-muted-foreground">Pincode</div><div>{detail.pincode || "—"}</div></div>
                  <div><div className="text-xs text-muted-foreground">Email</div><div>{detail.email || "—"}</div></div>
                  <div><div className="text-xs text-muted-foreground">Total</div><div className="font-medium">₹{Number(detail.total_order_amount).toFixed(2)}</div></div>
                  <div><div className="text-xs text-muted-foreground">To Collect</div><div>₹{Number(detail.amount_to_collect || 0).toFixed(2)}</div></div>
                  {Number(detail.discount_percentage || 0) > 0 && (
                    <div className="col-span-2 text-xs text-muted-foreground">
                      Discount applied: {Number(detail.discount_percentage).toFixed(2)}% (−₹{Number(detail.discount_amount || 0).toFixed(2)})
                    </div>
                  )}
                  {Number(detail.other_charges_amount || 0) > 0 && (
                    <div className="col-span-2 text-xs text-muted-foreground">
                      Includes other charges: ₹{Number(detail.other_charges_amount).toFixed(2)}
                      {detail.other_charges_note ? ` (${detail.other_charges_note})` : ""}
                    </div>
                  )}
                </div>
                {detail.items && detail.items.length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-2">Items</div>
                    <Table>
                      <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Qty</TableHead><TableHead>Price</TableHead><TableHead>Total</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {detail.items.map((i, idx) => (
                          <TableRow key={`${i.sku_id}-${idx}`}><TableCell>{i.sku_name}</TableCell><TableCell>{i.quantity}</TableCell><TableCell>₹{i.selling_price}</TableCell><TableCell>₹{i.line_total}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {detail.notes && (
                  <div><div className="text-xs text-muted-foreground">Notes</div><div className="whitespace-pre-wrap">{detail.notes}</div></div>
                )}
                {detail.dispatch_notes && (
                  <div><div className="text-xs text-muted-foreground">Notes from Operations</div><div className="whitespace-pre-wrap">{detail.dispatch_notes}</div></div>
                )}
                {detail.payment_screenshot && (
                  <div>
                    <a href={`/api/files/${detail.payment_screenshot}`} target="_blank" rel="noreferrer" className="text-sm underline text-blue-600">
                      View payment screenshot
                    </a>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <EditOrderDialog
        order={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={load}
      />
    </div>
  );
}
